// src/pages/admin/AllHero.jsx
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FaTrash } from "react-icons/fa";
import Cookies from "js-cookie";
import toast from "react-hot-toast";

const AllHero = () => {
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState(null); // ✅ track which hero is being deleted

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["hero-active"],
    queryFn: async () => {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/hero/active`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || "Something went wrong");
      return json;
    },
  });

  const { mutate: deleteHero, isPending: isDeletePending } = useMutation({
    mutationKey: ["hero-delete"],
    mutationFn: async (id) => {
      const token = Cookies.get("gameport");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/delete/${id}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || "Failed to delete hero");
      return json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hero-active"] });
      toast.success("Hero deleted successfully");
      setDeleteId(null);
    },
    onError: (err) => toast.error(err.message || "Delete failed"),
  });

  const heroes = data?.hero ?? [];
  const selectedHero = heroes.find((h) => h._id === deleteId);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-900">All Hero</h2>
        <span className="text-xs sm:text-sm text-gray-500">
          {heroes.length} item{heroes.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* Loading / Error */}
      {isPending && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 text-sm text-gray-600">
          Loading heroes...
        </div>
      )}

      {isError && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 text-sm bg-red 600">
          {error?.message || "Failed to load"}
        </div>
      )}

      {/* Empty */}
      {!isPending && !isError && heroes.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 text-sm text-gray-600">
          No hero items found.
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {heroes.map((h) => (
          <div
            key={h._id}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Left */}
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={h.image}
                  alt={h.title}
                  className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-xl border border-gray-100 shrink-0"
                />

                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {h.title || "Untitled"}
                  </h3>
                  {h.subtitle && (
                    <p className="text-sm text-gray-500 line-clamp-1">
                      {h.subtitle}
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteId(h._id)}
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <FaTrash className=" text-red rounded-full p-1 w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ✅ Fullscreen Confirm Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 "
            onClick={() => (isDeletePending ? null : setDeleteId(null))}
          />

          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-100 p-5 sm:p-6">
            <h4 className="text-base sm:text-lg font-bold text-gray-900">
              Delete hero?
            </h4>
            <p className="text-sm text-gray-600 mt-2">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900">
                {selectedHero?.title || "this hero"}
              </span>
              ? This action cannot be undone.
            </p>

            <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={() => setDeleteId(null)}
                disabled={isDeletePending}
                className="w-full sm:w-auto rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => deleteHero(deleteId)}
                disabled={isDeletePending}
                className="w-full sm:w-auto rounded-xl bg-red 600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red 700 disabled:opacity-60"
              >
                {isDeletePending ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllHero;
